import { useEffect, useState } from "react";
const { shell } = window.require("electron");

import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
} from "../../../components/ui/dialog";
import { Globe, Sparkles, X, BookOpen, Database, AlertCircle, AlertTriangle } from "lucide-react";
import { PromptExamplesModal } from "./PromptExamplesModal";
import KnowledgeBaseSection from "./KnowledgeBaseSection";
import { toast } from 'react-toastify';

const { channels, GEMINI_FILE_SEARCH_SUPPORTED_MODELS, GEMINI_THINKING_MODELS } = require('../../../shared/constants');

export const GeminiSettingsDialog = ({
	isOpen,
	onClose,
	geminiPrompt,
	geminiKey,
	onSave,
	geminiModel,
	temperature,
	webSearchEnabled,
	knowledgeBaseEnabled,
	thinkingLevel,
	mediaResolution,
	instanceId,
}) => {
	const ensureMinimumTemperature = (value) => Math.max(0.01, Number.parseFloat(value) || 0.01);

	const [localPrompt, setLocalPrompt] = useState(geminiPrompt);
	const [localGeminiKey, setLocalGeminiKey] = useState(geminiKey);
	const [localGeminiModel, setLocalGeminiModel] = useState(geminiModel || "gemini-2.5-flash");
	const [localTemperature, setLocalTemperature] = useState(ensureMinimumTemperature(temperature || 1.0));
	const [localWebSearchEnabled, setLocalWebSearchEnabled] = useState(webSearchEnabled || false);
	const [localKnowledgeBaseEnabled, setLocalKnowledgeBaseEnabled] = useState(knowledgeBaseEnabled || false);
	const [localThinkingLevel, setLocalThinkingLevel] = useState("high");
	const [localMediaResolution, setLocalMediaResolution] = useState("medium");
	const [isExamplesModalOpen, setIsExamplesModalOpen] = useState(false);
	const [isKnowledgeBaseOpen, setIsKnowledgeBaseOpen] = useState(false);
	const [activeStoreName, setActiveStoreName] = useState(null);

	const isFileSearchSupported = GEMINI_FILE_SEARCH_SUPPORTED_MODELS.includes(localGeminiModel);

	const handleSave = () => {
		onSave({
			prompt: localPrompt,
			geminiKey: localGeminiKey,
			geminiModel: localGeminiModel,
			temperature: localTemperature,
			webSearchEnabled: localWebSearchEnabled,
			knowledgeBaseEnabled: localKnowledgeBaseEnabled,
			thinkingLevel: localThinkingLevel,
			mediaResolution: localMediaResolution
		});
	};

	useEffect(() => {
		if (isOpen) {
			setLocalPrompt(geminiPrompt || "");
			setLocalGeminiKey(geminiKey || "");
			setLocalGeminiModel(geminiModel || "gemini-2.5-flash");
			setLocalTemperature(ensureMinimumTemperature(temperature || 1.0));
			setLocalWebSearchEnabled(webSearchEnabled || false);
			setLocalKnowledgeBaseEnabled(knowledgeBaseEnabled || false);
			setLocalThinkingLevel(thinkingLevel || "high");
			setLocalMediaResolution(mediaResolution || "medium");
		}
	}, [isOpen, geminiPrompt, geminiKey, geminiModel, temperature, webSearchEnabled, knowledgeBaseEnabled, thinkingLevel, mediaResolution]);

	useEffect(() => {
		if (isOpen && instanceId) {
			const { ipcRenderer } = window.require('electron');
			const { channels } = require('../../../shared/constants');

			ipcRenderer.invoke(channels.GET_KNOWLEDGE_STATUS, instanceId).then(result => {
				if (result.success) {
					setActiveStoreName(result.activeStoreName || null);
				}
			}).catch(err => {
				console.error('Error loading knowledge base status:', err);
			});
		}
	}, [isOpen, instanceId]);

	useEffect(() => {
		if (!isFileSearchSupported && localKnowledgeBaseEnabled) {
			setLocalKnowledgeBaseEnabled(false);
		}
	}, [localGeminiModel, isFileSearchSupported]);

	return (
		<>
			<Dialog
				open={isOpen}
				onOpenChange={(open) => {
					if (!open) {
						onClose();
					}
				}}
			>
				<DialogPortal>
					<DialogOverlay className="z-[8999] bg-black/80 backdrop-blur-md" />
					<DialogContent
						className="max-w-[850px] max-h-[90vh] bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-black/50 relative overflow-y-auto"
						style={{
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 9001
						}}
					>
					{/* Efeitos de fundo glassmorphism com gradiente escuro mais sutil */}
					<div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
					<div className="absolute inset-0 bg-gradient-to-r from-transparent via-modalOverlayStrong to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1500 pointer-events-none"></div>
					<div className="absolute top-16 left-24 w-40 h-40 bg-effectBlue rounded-full blur-3xl animate-pulse pointer-events-none" style={{animationDuration: '8s'}}></div>
					<div className="absolute bottom-24 right-20 w-48 h-48 bg-effectPurple rounded-full blur-3xl animate-pulse pointer-events-none" style={{animationDuration: '9s', animationDelay: '2s'}}></div>
					<div className="absolute top-40 right-40 w-28 h-28 bg-effectIndigo rounded-full blur-2xl animate-pulse pointer-events-none" style={{animationDuration: '6s', animationDelay: '4s'}}></div>
					<div className="absolute bottom-16 left-16 w-36 h-36 bg-effectCyan rounded-full blur-3xl animate-pulse pointer-events-none" style={{animationDuration: '7s', animationDelay: '3s'}}></div>
					{/* Botão X customizado */}
					<button
						onClick={() => {
							onClose();
						}}
						className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-white hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
					>
						<X className="w-4 h-4" />
					</button>

					<DialogHeader className="relative z-10">
						<DialogTitle className="flex items-center gap-4 text-white">
							<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800/80 via-statusInfo/30 to-purplePrimary/40 shadow-lg border border-white/20">
								<Sparkles className="w-6 h-6 text-statusInfo drop-shadow-sm" />
								<div className="absolute inset-0 bg-gradient-to-br from-statusInfo/10 to-purplePrimary/8 rounded-xl animate-pulse-subtle"></div>
							</div>
							<span className="text-xl font-semibold">Configurações do Gemini</span>
					</DialogTitle>
				</DialogHeader>
					<div className="flex flex-col gap-6 relative z-10">
						<div>
							<label
								htmlFor="gemini-key"
								className="block text-sm font-medium text-textOnDarkStrong mb-2"
							>
								Gemini Key
							</label>
							<input
								id="gemini-key"
								type="text"
								value={localGeminiKey}
								onChange={(e) => setLocalGeminiKey(e.target.value)}
								className="w-full rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText"
								placeholder="Digite sua Gemini Key"
							/>

							<span className="mt-2 block text-center text-xs text-gray-400">
								Gere uma gratuitamente em:{" "}
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										shell.openExternal("https://aistudio.google.com/app/apikey");
									}}
									className="text-statusInfo hover:text-statusInfo/80 transition-colors hover:underline"
								>
									Google AI Studio
								</button>{" "}
								<span className="italic text-gray-500">
									(grátis, sem cartão)
								</span>
							</span>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div>
								<label
									htmlFor="gemini-model"
									className="block text-sm font-medium text-textOnDarkStrong mb-2"
								>
									Modelo do Gemini
								</label>
								<select
									id="gemini-model"
									value={localGeminiModel}
									onChange={(e) => setLocalGeminiModel(e.target.value)}
									className="w-full rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg appearance-none cursor-pointer"
									style={{
										backgroundImage: `url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")`,
										backgroundPosition: 'right 0.5rem center',
										backgroundRepeat: 'no-repeat',
										backgroundSize: '1.5em 1.5em'
									}}
								>
									<option value="gemini-3.1-pro-preview" className="bg-slate-800 text-white">Gemini 3.1 Pro (Preview) 📚🧠</option>
									<option value="gemini-3-flash-preview" className="bg-slate-800 text-white">Gemini 3 Flash (Preview) 📚🧠</option>
									<option value="gemini-3.1-flash-lite-preview" className="bg-slate-800 text-white">Gemini 3.1 Flash-Lite (Preview) 📚🧠</option>
									<option value="gemini-3-pro-preview" className="bg-slate-800 text-white">Gemini 3 Pro (Preview) 📚🧠</option>
									<option value="gemini-2.5-flash" className="bg-slate-800 text-white">Gemini 2.5 Flash 📚</option>
									<option value="gemini-2.5-flash-lite" className="bg-slate-800 text-white">Gemini 2.5 Flash-Lite</option>
									<option value="gemini-2.5-pro" className="bg-slate-800 text-white">Gemini 2.5 Pro 📚</option>
									<option value="gemini-2.0-flash" className="bg-slate-800 text-white">Gemini 2.0 Flash (Deprecated)</option>
								</select>
							</div>

							<div>
								<label
									htmlFor="temperature"
									className="block text-sm font-medium text-textOnDarkStrong mb-2"
								>
									Temperatura: {localTemperature.toFixed(2)}
								</label>
								<div className="flex items-center">
									<span className="mr-2 text-xs text-gray-400">0.01</span>
									<input
										id="temperature"
										type="range"
										min="0.01"
										max="2"
										step="0.01"
										value={localTemperature}
										onChange={(e) => setLocalTemperature(ensureMinimumTemperature(e.target.value))}
										className="w-full h-2 bg-modalOverlaySoft backdrop-blur-xl rounded-lg appearance-none cursor-pointer accent-emeraldMedium shadow-lg border border-emeraldBase/20"
									/>
									<span className="ml-2 text-xs text-gray-400">2.0</span>
								</div>
								<div className="mt-1 text-xs text-center text-gray-400">
									{localTemperature <= 0.1 && 'Mais conservador e previsível'}
									{localTemperature > 0.1 && localTemperature < 1 && 'Equilibrado entre previsibilidade e criatividade'}
									{localTemperature >= 1 && localTemperature < 1.5 && 'Mais criativo e diverso'}
									{localTemperature >= 1.5 && 'Altamente criativo e imprevisível'}
								</div>
							</div>
						</div>

						{GEMINI_THINKING_MODELS.includes(localGeminiModel) && (
							<div className="rounded-xl border-2 border-purple-500/40 bg-gradient-to-br from-purple-900/20 via-indigo-900/20 to-blue-900/20 backdrop-blur-xl p-5 shadow-lg shadow-purple-500/10">
								<div className="flex items-center gap-2 mb-4">
									<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600/30 to-indigo-600/30 border border-purple-500/30">
										<span className="text-lg">🧠</span>
									</div>
									<h3 className="text-base font-semibold text-white">
										⚡ Configurações Exclusivas Gemini 3
									</h3>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>
										<label
											htmlFor="thinking-level"
											className="block text-sm font-medium text-textOnDarkStrong mb-2"
										>
											Nível de Raciocínio <span className="text-xs text-purple-300">(exclusivo gemini 3)</span>
										</label>
										<select
											id="thinking-level"
											value={localThinkingLevel}
											onChange={(e) => setLocalThinkingLevel(e.target.value)}
											className="w-full rounded-lg border border-purple-500/30 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-purple-400 focus:ring focus:ring-purple-400/30 shadow-lg appearance-none cursor-pointer"
											style={{
												backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
												backgroundPosition: 'right 0.5rem center',
												backgroundRepeat: 'no-repeat',
												backgroundSize: '1.5em 1.5em'
											}}
										>
											<option value="low" className="bg-slate-800 text-white">Low - Rápido e econômico</option>
											<option value="high" className="bg-slate-800 text-white">High - Raciocínio profundo</option>
										</select>
										<p className="mt-2 text-xs text-gray-400">
											Controla a profundidade do raciocínio interno do modelo
										</p>
									</div>

									<div>
										<label
											htmlFor="media-resolution"
											className="block text-sm font-medium text-textOnDarkStrong mb-2"
										>
											Resolução de Mídia <span className="text-xs text-purple-300">(exclusivo gemini 3)</span>
										</label>
										<select
											id="media-resolution"
											value={localMediaResolution}
											onChange={(e) => setLocalMediaResolution(e.target.value)}
											className="w-full rounded-lg border border-purple-500/30 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-purple-400 focus:ring focus:ring-purple-400/30 shadow-lg appearance-none cursor-pointer"
											style={{
												backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
												backgroundPosition: 'right 0.5rem center',
												backgroundRepeat: 'no-repeat',
												backgroundSize: '1.5em 1.5em'
											}}
										>
											<option value="low" className="bg-slate-800 text-white">Low - Processamento básico</option>
											<option value="medium" className="bg-slate-800 text-white">Medium - Balanceado</option>
											<option value="high" className="bg-slate-800 text-white">High - Máxima qualidade</option>
										</select>
										<p className="mt-2 text-xs text-gray-400">
											Controla a qualidade do processamento visual de imagens e vídeos
										</p>
									</div>
								</div>
							</div>
						)}

						<div
							className="rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl p-4 cursor-pointer hover:border-emeraldBase/40 transition-all duration-200"
							onClick={() => {
							if (!localWebSearchEnabled && localKnowledgeBaseEnabled) {
								setLocalKnowledgeBaseEnabled(false);
								toast.warning(
									'Base de Conhecimento desativada automaticamente para permitir a Pesquisa na Web. (Limitação da API)',
									{
										position: "top-right",
									}
								);
							}
							setLocalWebSearchEnabled(!localWebSearchEnabled);
						}}
						>
							<div className="flex items-center justify-between gap-4">
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										<Globe className="w-4 h-4 text-statusInfo" />
										<span className="text-sm font-medium text-white">Pesquisa na Web</span>
									</div>
									<p className="text-xs text-gray-300 leading-relaxed">
									  Ativa a habilidade do gemini de buscar na web.
										Ele só pesquisa quando detecta necessidade de informações atualizadas (eventos recentes, preços, dados em tempo real).
									</p>
								</div>
								<div className="relative inline-flex items-center pointer-events-none">
									<input
										type="checkbox"
										checked={localWebSearchEnabled}
										onChange={() => {}}
										className="sr-only peer"
										tabIndex={-1}
									/>
									<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
								</div>
							</div>
							{localWebSearchEnabled && (
								<div className="mt-3 flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
									<AlertTriangle className="w-4 h-4 flex-shrink-0" />
									<span>
										Enquanto ativa, as funcionalidades de Áudio por IA, Mídias por IA e Google Calendar serão desabilitadas (limitação da API Gemini).
									</span>
								</div>
							)}
						</div>

						<div className="rounded-lg border border-purple-500/25 bg-modalOverlaySoft backdrop-blur-xl p-4">
							<div 
								className={`flex items-center justify-between gap-4 mb-3 ${isFileSearchSupported ? 'cursor-pointer' : ''}`}
								onClick={() => {
									if (isFileSearchSupported) {
										if (!localKnowledgeBaseEnabled && localWebSearchEnabled) {
											setLocalWebSearchEnabled(false);
											toast.warning(
												'Pesquisa na Web desativada automaticamente para permitir a Base de Conhecimento. (Limitação da API)',
												{
													position: "top-right",
													autoClose: 5000
												}
											);
										}
										setLocalKnowledgeBaseEnabled(!localKnowledgeBaseEnabled);
									}
								}}
							>
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										<Database className="w-4 h-4 text-purple-400" />
										<span className="text-sm font-medium text-white">Base de Conhecimento</span>
										{activeStoreName && (
											<span className="px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30">
												{activeStoreName.split('/').pop()}
											</span>
										)}
									</div>
									<p className="text-xs text-gray-300 leading-relaxed">
										Permite ao Gemini consultar documentos enviados (PDFs, DOCX, etc) durante conversas.
										Você pode criar múltiplos stores, gerenciar arquivos em cada um, e selecionar qual store a IA utilizará.
									</p>
								</div>
								<div
									className={`relative inline-flex items-center ${isFileSearchSupported ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
								>
									<input
										type="checkbox"
										checked={localKnowledgeBaseEnabled}
										onChange={() => {}}
										disabled={!isFileSearchSupported}
										className="sr-only peer"
										tabIndex={-1}
									/>
									<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 peer-disabled:opacity-50"></div>
								</div>
							</div>
							<button
								type="button"
								disabled={!isFileSearchSupported}
								onClick={() => {
									if (!localGeminiKey || localGeminiKey.trim() === '') {
										alert('Configure a API Key do Gemini antes de gerenciar arquivos da base de conhecimento.');
										return;
									}
									setIsKnowledgeBaseOpen(true);
								}}
								className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600/20 to-purple-500/20 border border-purple-500/30 text-purple-300 transition-all text-sm ${isFileSearchSupported ? 'hover:from-purple-600/30 hover:to-purple-500/30 hover:text-purple-200 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
							>
								<Database className="w-4 h-4" />
								<span>Gerenciar Arquivos</span>
							</button>
							{!isFileSearchSupported && (
								<div className="mt-3 text-xs text-orange-300 flex items-start gap-2 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
									<AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
									<span>
										Base de Conhecimento disponível apenas para modelos compatíveis (marcados com 📚).
										Selecione um modelo compatível para usar esta funcionalidade.
									</span>
								</div>
							)}
							{localKnowledgeBaseEnabled && activeStoreName && (
								<div className="mt-3 flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
									<AlertTriangle className="w-4 h-4 flex-shrink-0" />
									<span>
										Enquanto ativa, as funcionalidades de Áudio por IA, Mídias por IA e Google Calendar serão desabilitadas (limitação da API Gemini).
									</span>
								</div>
							)}
						</div>

						<div>
							<div className="flex items-center justify-between mb-2">
								<label
									htmlFor="gemini-prompt"
									className="block text-sm font-medium text-textOnDarkStrong"
								>
									Prompt do Gemini
								</label>
								<button
									type="button"
									onClick={() => setIsExamplesModalOpen(true)}
									className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-statusInfo/20 to-purplePrimary/20 hover:from-statusInfo/30 hover:to-purplePrimary/30 border border-statusInfo/30 text-statusInfo hover:text-white transition-all duration-300 hover:scale-105 text-xs font-medium"
								>
									<BookOpen className="w-3.5 h-3.5" />
									<span>Exemplos de Prompts</span>
								</button>
							</div>
							<textarea
								id="gemini-prompt"
								className="w-full h-40 rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl p-4 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-gray-400"
								value={localPrompt}
								onChange={(e) => setLocalPrompt(e.target.value)}
								placeholder="Digite o prompt para o Gemini..."
							/>
						</div>
					</div>

					<div className="flex justify-end items-center space-x-3 pt-4 mt-2 relative z-10">
						<div className="flex space-x-3">
							<button
								type="button"
								onClick={onClose}
								className="relative rounded-xl text-white px-4 py-3 font-medium transition-all duration-300 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 shadow-lg hover:shadow-red-400/25 hover:scale-[1.02] cursor-pointer"
							>
								<span className="relative z-10">Cancelar</span>
							</button>
							<button
								onClick={handleSave}
								type="button"
								className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emeraldDark/80 to-emeraldMedium/80 backdrop-blur-xl border border-emeraldMedium/60 px-4 py-3 text-sm font-semibold text-white transition-all duration-300 hover:from-emeraldDark/90 hover:to-emeraldMedium/90 hover:border-emeraldMedium/70 hover:shadow-xl hover:shadow-emeraldMedium/40 shadow-lg shadow-emeraldMedium/25 hover:scale-[1.02]"
							>
								<svg className="h-5 w-5 drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
								</svg>
								<span>Salvar</span>
							</button>
						</div>
					</div>
					</DialogContent>
				</DialogPortal>
			</Dialog>

			<PromptExamplesModal
				isOpen={isExamplesModalOpen}
				onClose={() => setIsExamplesModalOpen(false)}
			/>

			<KnowledgeBaseSection
				isOpen={isKnowledgeBaseOpen}
				onClose={() => {
					setIsKnowledgeBaseOpen(false);
					if (isOpen && instanceId) {
						const { ipcRenderer } = window.require('electron');
						const { channels } = require('../../../shared/constants');
						ipcRenderer.invoke(channels.GET_KNOWLEDGE_STATUS, instanceId).then(result => {
							if (result.success) {
								setActiveStoreName(result.activeStoreName || null);
							}
						}).catch(err => {
							console.error('Error reloading knowledge base status:', err);
						});
					}
				}}
				onChange={() => {
					if (instanceId) {
						const { ipcRenderer } = window.require('electron');
						const { channels } = require('../../../shared/constants');
						ipcRenderer.invoke(channels.GET_KNOWLEDGE_STATUS, instanceId).then(result => {
							if (result.success) {
								setActiveStoreName(result.activeStoreName || null);
							}
						}).catch(err => {
							console.error('Error updating active store:', err);
						});
					}
				}}
				instanceId={instanceId}
			/>
		</>
	);
};
